import { cn } from '@/lib/utils';
import { ComponentProps } from 'react';

/**
 * Base skeleton component with shimmer animation
 */
function Skeleton({ className, ...props }: ComponentProps<'div'>) {
	return (
		<div
			data-slot='skeleton'
			className={cn(
				'bg-secondary/60 relative overflow-hidden rounded-md',
				// Shimmer effect
				'before:absolute before:inset-0 before:-translate-x-full',
				'before:animate-[shimmer_1.5s_infinite]',
				'before:via-muted-foreground/10 before:bg-gradient-to-r before:from-transparent before:to-transparent',
				className
			)}
			{...props}
		/>
	);
}

/**
 * Text line skeleton with customizable width
 */
function SkeletonText({
	className,
	width = 'full',
	...props
}: ComponentProps<'div'> & { width?: 'full' | 'lg' | 'md' | 'sm' | 'xs' }) {
	const widthClasses = {
		full: 'w-full',
		lg: 'w-3/4',
		md: 'w-1/2',
		sm: 'w-1/3',
		xs: 'w-1/4'
	};

	return (
		<Skeleton
			className={cn('h-4', widthClasses[width], className)}
			{...props}
		/>
	);
}

/**
 * Avatar/circle skeleton for icons and profile images
 */
function SkeletonAvatar({ className, size = 'md', ...props }: ComponentProps<'div'> & { size?: 'sm' | 'md' | 'lg' }) {
	const sizeClasses = {
		sm: 'h-6 w-6',
		md: 'h-8 w-8',
		lg: 'h-12 w-12'
	};

	return (
		<Skeleton
			className={cn('rounded-full', sizeClasses[size], className)}
			{...props}
		/>
	);
}

/**
 * Card skeleton for conversation items
 */
function SkeletonCard({ className, ...props }: ComponentProps<'div'>) {
	return (
		<div
			className={cn('flex flex-col gap-2', className)}
			{...props}
		>
			<Skeleton className='h-4 w-3/4' />
			<Skeleton className='h-3 w-1/2' />
		</div>
	);
}

/**
 * Button skeleton
 */
function SkeletonButton({ className, ...props }: ComponentProps<'div'>) {
	return (
		<Skeleton
			className={cn('h-10 w-24 rounded-lg', className)}
			{...props}
		/>
	);
}

/**
 * Sidebar conversation item skeleton
 */
function SkeletonConversationItem({ className, ...props }: ComponentProps<'div'>) {
	return (
		<div
			className={cn('flex items-center gap-3 rounded-md p-2', className)}
			{...props}
		>
			<Skeleton className='h-4 w-4 flex-shrink-0 rounded' />
			<Skeleton className='h-4 flex-1' />
		</div>
	);
}

export { Skeleton, SkeletonText, SkeletonAvatar, SkeletonCard, SkeletonButton, SkeletonConversationItem };
